/**
 * A server-drawn table that sorts in the browser (D173 of documentation/pr-plans/insights-simplify-plan.md):
 * Check my visitors' By topic, Target keywords' topics, Search ideas and Win back clicks' searches. Wrapped by
 * <x-insights.sortable-rows>, whose headers are <x-insights.sortable-header>, so they sort, look and are said as
 * Stats' tables and the activity tables are.
 *
 * Each row of the body (x-ref="rows") carries its figures unformatted as JSON in data-sort and its place in the
 * server's order in data-sort-index; a header pressed puts the rows in that column's order by moving them, and
 * says the order once in the wrapper's polite region. The rows arrive in the server's order, which `initial`
 * names when one of the columns is it, so its header says so until another is pressed.
 *
 * Livewire redraws the rows in the server's order (its morph moves keyed rows back), so the body is watched and
 * the order on screen is put back after each redraw; a body already in order is left alone, so the watch never
 * answers its own moves.
 */

import { ASCENDING, DESCENDING, ariaSort, nextSort, sortArrow, sortRows, sortSentence } from './sortable-header';

/** A row's figure for one column, from its data-sort JSON, or null when it has none. */
export function rowValue(row, key) {
    try {
        const value = JSON.parse(row?.dataset?.sort ?? '{}')?.[key];

        return value === undefined ? null : value;
    } catch {
        return null;
    }
}

/**
 * The body's rows in the order: from the server's order (data-sort-index), so ties keep it whatever order the
 * rows are on screen in.
 */
export function orderedRows(rows, sort) {
    const served = [...rows].sort((a, b) => Number(a.dataset.sortIndex) - Number(b.dataset.sortIndex));

    return sort ? sortRows(served, sort, (row) => rowValue(row, sort.key)) : served;
}

/**
 * @param {object} config
 * @param {object} config.columns     key -> { label, kind }: kind 'text' (A to Z first), 'position' (top of Google
 *                                    first) or 'number' (highest first)
 * @param {object|null} config.initial  the column and way the server ordered the rows by, or null for none
 * @param {string} config.sorted      activity_table.sorted ("Sorted by :column, :direction.")
 * @param {object} config.directions  activity_table.direction
 */
export default ({ columns = {}, initial = null, sorted = '', directions = {} } = {}) => {
    let observer = null;

    return {
        sort: initial,

        announcement: '',

        init() {
            const body = this.$refs.rows;

            if (!body || typeof MutationObserver !== 'function') return;

            observer = new MutationObserver(() => this.arrange());
            observer.observe(body, { childList: true });
        },

        destroy() {
            observer?.disconnect();
        },

        isSortable(key) {
            return columns[key] !== undefined;
        },

        ariaSortOf(key) {
            return this.isSortable(key) ? ariaSort(this.sort, key) : null;
        },

        sortArrowOf(key) {
            return this.isSortable(key) ? sortArrow(this.sort, key) : '';
        },

        /** A header pressed: the rows in its column's order, or the other way when it is the one on; said once. */
        sortBy(key) {
            if (!this.isSortable(key)) return;

            const kind = columns[key].kind;

            this.sort = nextSort(this.sort, key, kind === 'number' ? DESCENDING : ASCENDING);
            this.arrange();

            const sentence = sortSentence(sorted, directions, columns[key].label ?? key, this.sort.direction, kind === 'number' ? null : kind);

            // Emptied first, so the same sentence twice is said twice.
            this.announcement = '';
            this.$nextTick(() => {
                this.announcement = sentence;
            });
        },

        /** The body's rows put in the order on screen, moved only when they are not in it already. */
        arrange() {
            const body = this.$refs.rows;

            if (!body) return;

            const rows = [...body.children].filter((row) => row.hasAttribute('data-sort'));
            const ordered = orderedRows(rows, this.sort);

            if (ordered.every((row, index) => rows[index] === row)) return;

            ordered.forEach((row) => body.appendChild(row));
            observer?.takeRecords();
        },
    };
};
